import {
  Component,
  computed,
  effect,
  inject,
  signal,
  untracked,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { rxResource, toSignal } from '@angular/core/rxjs-interop';
import { debounceTime, catchError } from 'rxjs/operators';
import { finalize, forkJoin, of } from 'rxjs';
import { PurchaseEditModalComponent } from '../purchase-edit-modal/purchase-edit-modal.component';

import { NzTableModule, NzTableQueryParams } from 'ng-zorro-antd/table';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzSpaceModule } from 'ng-zorro-antd/space';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { NzProgressModule } from 'ng-zorro-antd/progress';
import { NzPopconfirmModule } from 'ng-zorro-antd/popconfirm';
import { NzPageHeaderModule } from 'ng-zorro-antd/page-header';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzEmptyModule } from 'ng-zorro-antd/empty';
import { NzModalService } from 'ng-zorro-antd/modal';
import { NzDrawerService } from 'ng-zorro-antd/drawer';
import { NzMessageService } from 'ng-zorro-antd/message';
import { openPackagePurchaseDrawer } from '../package-purchase-drawer/open-package-drawer';
import { openPackageRefundModal } from '../package-refund-modal/open-package-refund-modal';
import { openPackageCancelModal } from '../package-cancel-modal/open-package-cancel-modal';
import { isEarlierDay } from '../shared/settlement-day';

import { SelectComponent, SelectSearchComponent } from '@beautyapp/form';
import {
  ServicePackageService,
  ServicePackagePurchase,
  ServicePackagePurchaseStatus,
  ServiceService,
  CustomerService,
  PackageLifecyclePipe,
  PackageLifecycleColorPipe,
} from '@beautyapp/core';

@Component({
  selector: 'beautyapp-package-purchases',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    PackageLifecyclePipe,
    PackageLifecycleColorPipe,
    NzTableModule,
    NzButtonModule,
    NzIconModule,
    NzTagModule,
    NzCardModule,
    NzSpaceModule,
    NzToolTipModule,
    NzProgressModule,
    NzPopconfirmModule,
    NzPageHeaderModule,
    NzInputModule,
    NzDatePickerModule,
    NzEmptyModule,
    SelectComponent,
    SelectSearchComponent,
  ],
  templateUrl: './package-purchases.component.html',
})
export class PackagePurchasesComponent {
  private packageService = inject(ServicePackageService);
  private serviceService = inject(ServiceService);
  private customerService = inject(CustomerService);
  private router = inject(Router);
  private modal = inject(NzModalService);
  private drawerService = inject(NzDrawerService);
  private message = inject(NzMessageService);
  private translate = inject(TranslateService);

  constructor() {
    // Any filter change returns the user to page 1, so they aren't stranded on
    // a page index that no longer exists in the narrowed result set.
    effect(() => {
      this.filterValues();
      untracked(() => this.page.set(1));
    });
  }

  filterForm = new FormGroup({
    q: new FormControl<string | null>(null),
    userId: new FormControl<string | null>(null),
    packageId: new FormControl<string | null>(null),
    status: new FormControl<string | null>(null),
    dateRange: new FormControl<Date[] | null>(null),
  });

  // Customers matched by the server-search picker. Populated on demand from the
  // typed term (same `users/search` endpoint the sell-package flow uses).
  customers = signal<Array<{ id: string; name: string }>>([]);

  onCustomerSearch(search: string): void {
    if (!search || search.length < 2) {
      this.customers.set([]);
      return;
    }
    this.customerService.search(search).subscribe({
      next: (users: any[]) => {
        this.customers.set(
          (users ?? []).map((u: any) => ({
            id: u.id,
            name: u.fullName + (u.mobilePhone ? ` - ${u.mobilePhone}` : ''),
          }))
        );
      },
    });
  }

  page = signal(1);
  pageSize = signal(10);

  // Package templates for the filter dropdown (id → name). Loaded once.
  private packagesResource = rxResource({
    params: () => ({}),
    stream: () =>
      this.packageService
        .getAll({ pageSize: 200 })
        .pipe(catchError(() => of({ data: [], meta: {} as any }))),
  });

  packageOptions = computed(() =>
    (this.packagesResource.value()?.data ?? []).map((p) => ({
      label: p.name,
      value: p.id,
    }))
  );

  // In-flight guard for the edit modal — blocks the rapid-click modal stacking
  // (#2908 §4) during the async forkJoin before modalService.create runs.
  editOpening = signal(false);

  statusOptions = computed(() => [
    {
      label: this.translate.instant('servicePackages.status.ACTIVE'),
      value: 'ACTIVE',
    },
    {
      label: this.translate.instant('servicePackages.status.USED'),
      value: 'USED',
    },
    {
      label: this.translate.instant('servicePackages.status.CANCELLED'),
      value: 'CANCELLED',
    },
    {
      label: this.translate.instant('servicePackages.status.EXPIRED'),
      value: 'EXPIRED',
    },
  ]);

  filterValues = toSignal(
    this.filterForm.valueChanges.pipe(debounceTime(300)),
    { initialValue: this.filterForm.value }
  );

  purchasesResource = rxResource({
    params: () => {
      const filters = this.filterValues();
      const range = filters?.dateRange ?? null;
      return {
        page: this.page(),
        pageSize: this.pageSize(),
        q: filters?.q?.trim() || undefined,
        userId: filters?.userId || undefined,
        packageId: filters?.packageId || undefined,
        status: filters?.status || undefined,
        from: range?.[0] ? this.startOfDayIso(range[0]) : undefined,
        to: range?.[1] ? this.endOfDayIso(range[1]) : undefined,
      };
    },
    stream: ({ params }) =>
      this.packageService.getPurchases(params).pipe(
        catchError(() =>
          of({
            data: [],
            meta: { page: 1, pageSize: 10, pageCount: 0, itemCount: 0 },
          })
        )
      ),
  });

  // How many filters are active — drives the Clear button badge and the
  // "no matches" vs "nothing yet" empty-state copy.
  activeFilterCount = computed(() => {
    const f = this.filterValues();
    return [
      f?.q?.trim(),
      f?.userId,
      f?.packageId,
      f?.status,
      f?.dateRange?.[0],
    ].filter(Boolean).length;
  });

  private startOfDayIso(d: Date): string {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x.toISOString();
  }

  private endOfDayIso(d: Date): string {
    const x = new Date(d);
    x.setHours(23, 59, 59, 999);
    return x.toISOString();
  }

  loading = computed(() => this.purchasesResource.isLoading());
  purchases = computed(() => this.purchasesResource.value()?.data || []);
  total = computed(() => this.purchasesResource.value()?.meta?.itemCount || 0);

  onQueryParamsChange(params: NzTableQueryParams): void {
    this.page.set(params.pageIndex);
    this.pageSize.set(params.pageSize);
  }

  clearFilters(): void {
    this.filterForm.reset();
    this.page.set(1);
  }

  goBack(): void {
    this.router.navigate(['/service-packages']);
  }

  getStatusColor(status: ServicePackagePurchaseStatus): string {
    const colors: Record<string, string> = {
      ACTIVE: 'green',
      USED: 'blue',
      CANCELLED: 'red',
      EXPIRED: 'default',
    };
    return colors[status] || 'default';
  }

  getUsagePercent(purchase: ServicePackagePurchase): number {
    if (purchase.totalServicesCount === 0) return 0;
    return Math.round(
      (purchase.usedServicesCount / purchase.totalServicesCount) * 100
    );
  }

  viewDetails(purchase: ServicePackagePurchase): void {
    // Opens the shared package drawer (info header + services + edit history),
    // superseding the old details-only modal.
    openPackagePurchaseDrawer(this.drawerService, purchase.id);
  }

  /**
   * Fully consumed = every service line used up. Edit/cancel are hidden for
   * these even if the stored status still reads ACTIVE (a denormalized-status
   * drift; see migration 1782700000000), so the row never offers an action the
   * backend will reject.
   */
  isFullyUsed(purchase: ServicePackagePurchase): boolean {
    return (
      purchase.totalServicesCount > 0 &&
      purchase.usedServicesCount >= purchase.totalServicesCount
    );
  }

  cancelPurchase(purchase: ServicePackagePurchase): void {
    // beaut-290: a USED package can't be plain-cancelled (the BE rejects it) —
    // it must go through the refund engine where the operator chooses the return
    // type + method and sees the computed amount. A fully-unused package keeps
    // the lightweight confirm → revert path.
    if ((purchase.usedServicesCount ?? 0) > 0) {
      openPackageRefundModal(this.modal, purchase).afterClose.subscribe(
        (refunded) => {
          if (refunded) this.purchasesResource.reload();
        }
      );
      return;
    }

    // beaut-484: an unused package sold on an earlier day asks which till day
    // its reversal lands on, as the refund modal does for a used one. Sold
    // today, both answers are the same till, so the plain confirm stays.
    if (isEarlierDay(purchase.purchasedAt ?? purchase.createdAt)) {
      openPackageCancelModal(this.modal, this.translate, purchase).afterClose.subscribe(
        (cancelled) => {
          if (cancelled) this.purchasesResource.reload();
        }
      );
      return;
    }

    this.modal.confirm({
      nzTitle: this.translate.instant('servicePackages.confirm.cancel'),
      nzContent: this.translate.instant(
        'servicePackages.confirm.cancelDescription'
      ),
      nzOkDanger: true,
      nzOnOk: () => {
        this.packageService.cancelPurchase(purchase.id, {}).subscribe({
          next: () => {
            this.message.success(
              this.translate.instant('servicePackages.messages.cancelled')
            );
            this.purchasesResource.reload();
          },
          error: (err) => {
            this.message.error(
              err.error?.message ||
                this.translate.instant('servicePackages.messages.cancelError')
            );
          },
        });
      },
    });
  }

  openEditModal(purchase: ServicePackagePurchase): void {
    if (this.editOpening()) {
      return;
    }
    this.editOpening.set(true);
    forkJoin({
      detail: this.packageService.getPurchaseById(purchase.id),
      catalog: this.serviceService.dropdown(),
    })
      .pipe(finalize(() => this.editOpening.set(false)))
      .subscribe({
        next: ({ detail, catalog }) => {
          const purchaseItems = (detail.services ?? []).map((s: any) => ({
            id: s.packageItemId,
            serviceId: s.serviceId,
            serviceName: s.serviceName,
            // Clamp to the domain floor (≥1). A legacy row with a null/0
            // quantity would otherwise hydrate the required+min(1) control as
            // an empty, invalid field that the operator can't easily fix.
            quantity: Number(s.totalQuantity) || 1,
            originalPrice: s.originalPrice,
            discountedPrice: s.discountedPrice,
          }));
          const rawCatalog: any[] = catalog?.data ?? catalog ?? [];
          const servicesCatalog = rawCatalog.map((s: any) => ({
            id: s.id,
            name: s.name,
            price: Number(s.price ?? 0),
            discountPrice: Number(s.discountPrice ?? s.price ?? 0),
          }));
          this.modal
            .create({
              nzTitle: 'პაკეტის რედაქტირება',
              nzContent: PurchaseEditModalComponent,
              nzWidth: 800,
              nzData: {
                purchaseId: detail.id,
                initialPurchase: {
                  ...detail,
                  displayName:
                    (detail as any).displayName ?? detail?.package?.name ?? '',
                  comment: detail.comment ?? null,
                  purchaseItems,
                  usages: detail.usages ?? [],
                },
                servicesCatalog,
              },
              nzFooter: null,
            })
            .afterClose.subscribe((result) => {
              if (result?.success) {
                this.purchasesResource.reload();
              }
            });
        },
        error: () => {
          this.message.error(
            this.translate.instant('servicePackages.messages.loadError')
          );
        },
      });
  }
}
